/**
 * Sparkles Effect Component
 * Animated sparkle particles for festive themes
 */

import { useEffect, useRef } from 'react'

interface SparklesProps {
  color?: string
}

interface Sparkle {
  x: number
  y: number
  size: number
  opacity: number
  speed: number
  life: number
  maxLife: number
}

export function Sparkles({ color = 'hsl(var(--accent))' }: SparklesProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const sparklesRef = useRef<Sparkle[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Create new sparkle
    const createSparkle = (): Sparkle => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 3 + 2, // 2-5px
      opacity: 1,
      speed: Math.random() * 0.02 + 0.01,
      life: 0,
      maxLife: Math.random() * 100 + 50, // 50-150 frames
    })

    // Initialize sparkles
    const initSparkles = () => {
      sparklesRef.current = []
      for (let i = 0; i < 20; i++) {
        sparklesRef.current.push(createSparkle())
      }
    }

    initSparkles()

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      sparklesRef.current.forEach((sparkle, index) => {
        // Update life
        sparkle.life += 1

        // Fade in and out
        if (sparkle.life < sparkle.maxLife * 0.3) {
          sparkle.opacity = sparkle.life / (sparkle.maxLife * 0.3)
        } else if (sparkle.life > sparkle.maxLife * 0.7) {
          sparkle.opacity = 1 - (sparkle.life - sparkle.maxLife * 0.7) / (sparkle.maxLife * 0.3)
        } else {
          sparkle.opacity = 1
        }

        // Reset if life expired
        if (sparkle.life >= sparkle.maxLife) {
          sparklesRef.current[index] = createSparkle()
        }

        // Draw sparkle (star shape)
        ctx.save()
        ctx.translate(sparkle.x, sparkle.y)
        ctx.rotate((sparkle.life * 0.1) % (Math.PI * 2))
        ctx.globalAlpha = sparkle.opacity

        // Draw star
        ctx.beginPath()
        for (let i = 0; i < 5; i++) {
          const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2
          const x = Math.cos(angle) * sparkle.size
          const y = Math.sin(angle) * sparkle.size
          if (i === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
        }
        ctx.closePath()
        ctx.fillStyle = color
        ctx.fill()

        ctx.restore()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [color])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0, // Behind content but above background
      }}
      aria-hidden="true"
    />
  )
}

